import * as React from 'react';
import { useRef, useState } from 'react';
import { addVoucherOrPromotionToTransaction } from 'Services/Api/_HumanWritten/GiftCertificateService';
import { fetchGiftCertificateUsagesByTransactionId } from 'Util/_HumanWritten/FetchGiftCertificates';
import { GiftCertificateUsageEntity } from 'Models/Entities';
import { observer } from 'mobx-react';
import PromoGiftCardInput, {
	GiftCertificateProps,
	PromoGiftCardMessage,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/PromoGiftCardInput';
import {
	PROMOTION_NOT_ELIGIBLE_CUSTOMER_MESSAGE,
	PROMOTION_USAGE_LIMIT_REACHED_MESSAGE,
} from 'Util/_HumanWritten/PromotionErrorMessages';

export interface PurchasePromoGiftCardProps {
	transactionId: string;
	/** Called after a successful apply so the caller can re-price the purchase. */
	onApplied?: () => void;
	/** Receives the transaction's usages after an apply, for rendering the applied-credit list. */
	updateGiftCertUsages?: (newEntities: GiftCertificateUsageEntity[]) => Promise<void> | void;
	forCheckIn?: boolean;
}

/**
 * The voucher input for a non-booking purchase — an anytime ticket (#2613) or parking (#2614).
 *
 * Wraps the same {@link PromoGiftCardInput} the ferry cart uses, and calls the same generic
 * `add-to-transaction` endpoint; only the error mapping differs. The booking-shaped branches
 * (`NOT_FERRY_BOOKING`, `RETURN_BOOKING_DISCOUNT`, `NOT_SAME_DAY_RETURN`, the trip-departure-window
 * warning, …) cannot occur on a transaction that carries no alterations, so they are left out rather
 * than copied across — a message that cannot fire is a message that will drift.
 */
function PurchasePromoGiftCard({
	transactionId,
	onApplied,
	updateGiftCertUsages,
	forCheckIn = false,
}: PurchasePromoGiftCardProps) {
	const [certificateProps, setCertificateProps] = useState<GiftCertificateProps>({
		code: '',
		loading: false,
	});
	const [message, setMessage] = useState<PromoGiftCardMessage | null>(null);
	const applyingRef = useRef(false);

	const onApply = async () => {
		if (applyingRef.current || !certificateProps.code?.trim()) {
			return;
		}
		applyingRef.current = true;
		setCertificateProps(prevState => ({ ...prevState, loading: true }));

		try {
			await addVoucherOrPromotionToTransaction(transactionId, certificateProps.code, forCheckIn, false);

			if (updateGiftCertUsages) {
				try {
					const { data } = await fetchGiftCertificateUsagesByTransactionId(transactionId);
					await updateGiftCertUsages(data ?? []);
				} catch (e) {
					console.error(e);
				}
			}
			if (onApplied) {
				onApplied();
			}

			applyingRef.current = false;
			setCertificateProps({ code: '', loading: false });
			setMessage({ text: 'Voucher applied', type: 'success' });
		} catch (e: any) {
			const errorCode: string = e.response?.data ?? '';
			let messageText = 'Invalid voucher';
			let messageType: PromoGiftCardMessage['type'] = 'error';

			applyingRef.current = false;
			setCertificateProps({ code: '', loading: false });

			switch (errorCode) {
				case 'BAD_CODE':
					messageText = 'Voucher does not exist';
					break;
				case 'EMPTY':
					messageText = 'Voucher has no remaining value';
					break;
				case 'EXPIRED':
					messageText = 'Voucher is expired';
					break;
				case 'ALREADY_APPLIED':
					messageText = 'This voucher is already applied to this purchase';
					break;
				case 'NOT_REQUIRED':
					messageText = 'This purchase does not require payment';
					break;
				case 'NOT_APPLICABLE':
					messageText = 'This code does not reduce the price of this purchase.';
					messageType = 'warning';
					break;
				case 'SAVE_FAILED':
					messageText = "Couldn't apply the voucher right now. Please try again.";
					break;
				case 'USAGE_LIMIT_REACHED':
					messageText = PROMOTION_USAGE_LIMIT_REACHED_MESSAGE;
					break;
				case 'NOT_ELIGIBLE_CUSTOMER':
					messageText = PROMOTION_NOT_ELIGIBLE_CUSTOMER_MESSAGE;
					break;
				case 'INVALID_CODE':
					messageText = 'Invalid voucher code';
					break;
				default:
					break;
			}

			setMessage({ text: messageText, type: messageType });
		}
	};

	return (
		<PromoGiftCardInput
			certificateProps={certificateProps}
			setCertificateProps={setCertificateProps}
			onApply={onApply}
			message={message}
			onCodeChange={() => setMessage(null)}
		/>
	);
}

export default observer(PurchasePromoGiftCard);
